import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as RedirectActions from '../../../actions/RedirectActions';
import * as StatusManagerActions from '../../../actions/StatusManagerActions';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    Row,
    Select,
    Spin,
    Table,
    Typography,
    message,
} from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/pt-br';
import ptBR from 'antd/lib/date-picker/locale/pt_BR';
import BreadcrumbAdapter from '../../adapters/BreadcrumbAdapter';
import TemplateAdapter from '../../adapters/TemplateAdapter';
import config, { getBasicAuthHeaders } from '../../../utils/configAdapter';
import { mask } from '../../../utils/helpers';
import Core from '../../../class/Core.js';

dayjs.extend(customParseFormat);
dayjs.locale('pt-br');

const { RangePicker } = DatePicker;
const INITIAL_FILTERS = {
    tipo: 'SIM',
    pagos: 'SIM',
};

const AdicionaisLogs = ({ redirect, action }) => {
    const [form] = Form.useForm();
    const [adicionais, setAdicionais] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [paramsExcel, setParamsExcel] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [dateRange, setDateRange] = useState({
        dataIni: '',
        dataFim: '',
    });

    useEffect(() => {
        // Mostra o menu lateral e limpa o redirect do Redux para não ficar preso em loop nesta rota
        action.showManager();
        action.redirectClear();
        const core = new Core();
        core.isAuth();
        form.setFieldsValue(INITIAL_FILTERS);
        carregarAdicionais(core);
        hidratarBuscaPorUrl();
    }, []);

    useEffect(() => {
        if (hasSearched) {
            buscar(form.getFieldsValue(), page, pageSize);
        }
    }, [page, pageSize]);

    const hidratarBuscaPorUrl = () => {
        if (!window.location.search) {
            return;
        }

        const urlParams = Object.fromEntries(
            new URLSearchParams(window.location.search),
        );

        if (urlParams.adic) {
            urlParams.blto_adic = urlParams.adic;
        }

        if (urlParams.s) {
            urlParams.empresa_pesquisa = urlParams.s;
        }

        const values = {
            ...INITIAL_FILTERS,
            ...urlParams,
        };

        if (urlParams.dataIni && urlParams.dataFim) {
            values.periodo = [
                dayjs(urlParams.dataIni, 'YYYY-MM-DD'),
                dayjs(urlParams.dataFim, 'YYYY-MM-DD'),
            ];
            setDateRange({
                dataIni: urlParams.dataIni,
                dataFim: urlParams.dataFim,
            });
        }

        form.setFieldsValue(values);

        if (Object.keys(urlParams).length) {
            setHasSearched(true);
            buscar(values, 1, pageSize, {
                dataIni: urlParams.dataIni || '',
                dataFim: urlParams.dataFim || '',
            });
        }
    };

    const carregarAdicionais = async (core = new Core()) => {
        try {
            // Pega o usuário logado para o backend resolver o escritório
            const user = core.getAuth();
            let response = await fetch(
                `${config[process.env.NODE_ENV].url}/juridico/adicionais?usuario=${user.key}`,
                {
                    method: 'GET',
                    headers: getBasicAuthHeaders(),
                },
            );

            if (!response.ok) {
                throw await response.json();
            }

            response = await response.json();

            setAdicionais(
                (response.content || [])
                    .slice()
                    .sort((a, b) => b.id_adic - a.id_adic)
                    .map((item) => ({
                        value: `${item.id_adic}`,
                        label: `${item.nome_adic}`,
                    })),
            );
        } catch (error) {
            message.error(error?.message || 'Erro ao carregar adicionais.');
        }
    };

    const buscar = async (
        values,
        nextPage = page,
        nextPageSize = pageSize,
        rangeOverride,
    ) => {
        try {
            const payload = {
                ...values,
                dataIni: rangeOverride ? rangeOverride.dataIni : dateRange.dataIni,
                dataFim: rangeOverride ? rangeOverride.dataFim : dateRange.dataFim,
                page: nextPage,
                pageSize: nextPageSize,
            };

            delete payload.periodo;

            const searchParams = new URLSearchParams(payload).toString();

            setLoading(true);
            setParamsExcel(searchParams);

            let response = await fetch(
                `${config[process.env.NODE_ENV].url}/adicionais/relatorios/empresas-adesao?${searchParams}`,
                {
                    method: 'GET',
                    headers: getBasicAuthHeaders(),
                },
            );

            if (!response.ok) {
                throw await response.json();
            }

            response = await response.json();

            setDataSource(response.content || []);
            setTotal(response.total || 0);
        } catch (error) {
            message.error(error?.message || 'Erro ao buscar dados.');
        } finally {
            setLoading(false);
        }
    };

    const onFinish = async () => {
        const values = await form.validateFields();
        setHasSearched(true);
        setPage(1);
        buscar(values, 1, pageSize);
    };

    const limparBusca = () => {
        form.resetFields();
        form.setFieldsValue(INITIAL_FILTERS);
        setDataSource([]);
        setHasSearched(false);
        setTotal(0);
        setPage(1);
        setPageSize(10);
        setParamsExcel('');
        setDateRange({ dataIni: '', dataFim: '' });
    };

    const columns = [
        {
            title: 'CNPJ/CPF',
            dataIndex: 'cnpj_empr',
            key: 'cnpj_empr',
            width: 160,
            render: (value) => (value ? mask(value) : '-'),
        },
        {
            title: 'Empregador',
            dataIndex: 'razao_empr',
            key: 'razao_empr',
        },
        {
            title: 'ID Cob.',
            dataIndex: 'id_empr_blto',
            key: 'id_empr_blto',
            width: 100,
        },
        {
            title: 'Blto. Pago',
            dataIndex: 'fl_pago_empr_blto',
            key: 'fl_pago_empr_blto',
            width: 110,
            render: (value) => (value ? 'SIM' : 'NÃO'),
        },
        {
            title: 'Data Vencimento',
            dataIndex: 'dt_vcto_empr_blto',
            key: 'dt_vcto_empr_blto',
            width: 140,
            render: (value) => (value ? mask(value, 'date') : '-'),
        },
        {
            title: 'Aceite',
            dataIndex: 'aceite_empr_adic_adesao',
            key: 'aceite_empr_adic_adesao',
            width: 90,
            render: (value) => (value === 'SIM' ? 'SIM' : 'NÃO'),
        },
        {
            title: 'Data do Aceite',
            dataIndex: 'dt_frt_empr_adic_adesao',
            key: 'dt_frt_empr_adic_adesao',
            width: 170,
            render: (value) =>
                value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
        },
        {
            title: 'Plano',
            dataIndex: 'id_tr_plno',
            key: 'id_tr_plno',
            width: 80,
        },
        {
            title: 'Usuário',
            key: 'usuario',
            width: 260,
            render: (_, row) => {
                const operador = row?.body_empr_adic_adesao?.operador;

                if (!operador) {
                    return '-';
                }

                return (
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                        <li>
                            <strong>CPF</strong>: {operador.cpf}
                        </li>
                        <li>
                            <strong>NOME</strong>: {operador.nome}
                        </li>
                    </ul>
                );
            },
        },
    ];

    if (redirect.url) {
        return <Redirect to={redirect.url} />;
    }

    return (
        <TemplateAdapter>
            <BreadcrumbAdapter
                data={[
                    {
                        titulo: 'Jurídico',
                        link: '/manager/dashboard',
                    },
                    { titulo: 'Adicionais Logs' },
                ]}
            />

            <Card style={{ marginBottom: 24 }}>
                <Typography.Title level={2} style={{ marginBottom: 8 }}>
                    Logs de Adesão de Empregadores
                </Typography.Title>
                <Typography.Text type='secondary'>
                    Tela para consulta completa dos logs de adesão de
                    empregadores aos adicionais.
                </Typography.Text>
            </Card>

            <Spin spinning={loading}>
                <Form
                    form={form}
                    layout='vertical'
                    initialValues={INITIAL_FILTERS}
                    onFinish={onFinish}
                >
                    <Row gutter={12}>
                        <Col xs={24} md={12} lg={12}>
                            <Form.Item
                                name='blto_adic'
                                label='Adicional'
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            'Por favor, selecione um adicional!',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder='Selecione'
                                    options={adicionais}
                                    showSearch
                                    filterOption={(input, option) => {
                                        const target = `${option?.value || ''}${option?.label || ''}`;
                                        return target
                                            .toLowerCase()
                                            .includes(input.toLowerCase());
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6} lg={6}>
                            <Form.Item name='tipo' label='Tipo'>
                                <Select
                                    options={[
                                        { label: 'TODOS', value: 'TODOS' },
                                        { label: 'Aderiu', value: 'SIM' },
                                        {
                                            label: 'Não aderiu',
                                            value: 'NAO',
                                        },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6} lg={6}>
                            <Form.Item name='pagos' label='Pagamentos'>
                                <Select
                                    options={[
                                        { label: 'TODOS', value: 'TODOS' },
                                        { label: 'PAGOS', value: 'SIM' },
                                        {
                                            label: 'NÃO PAGOS',
                                            value: 'NAO',
                                        },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={12}>
                            <Form.Item
                                name='empresa_pesquisa'
                                label='Razão Social/Nome ou CNPJ/CPF'
                            >
                                <Input placeholder='Busca' />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={12}>
                            <Form.Item
                                name='periodo'
                                label='Selecione um Período do Aceite'
                            >
                                <RangePicker
                                    locale={ptBR}
                                    format='DD/MM/YYYY'
                                    style={{ width: '100%' }}
                                    disabledDate={(current) =>
                                        current &&
                                        current > dayjs().endOf('day')
                                    }
                                    onChange={(dates) => {
                                        if (dates && dates.length === 2) {
                                            setDateRange({
                                                dataIni: dates[0].format(
                                                    'YYYY-MM-DD',
                                                ),
                                                dataFim: dates[1].format(
                                                    'YYYY-MM-DD',
                                                ),
                                            });
                                            return;
                                        }

                                        setDateRange({
                                            dataIni: '',
                                            dataFim: '',
                                        });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div style={{ marginBottom: 16 }}>
                        {paramsExcel ? (
                            <a
                                href={`${config[process.env.NODE_ENV].url}/adicionais/relatorios/empresas-adesao?${paramsExcel}&excel=true`}
                                download
                                onClick={(event) => {
                                    if (loading) {
                                        event.preventDefault();
                                    }
                                }}
                                style={{ marginRight: 8 }}
                            >
                                <Button disabled={loading}>
                                    Download XLSX
                                </Button>
                            </a>
                        ) : null}

                        <Button
                            onClick={limparBusca}
                            disabled={loading}
                            style={{ marginRight: 8 }}
                        >
                            Limpar Busca
                        </Button>

                        <Button
                            type='primary'
                            htmlType='submit'
                            loading={loading}
                        >
                            Buscar
                        </Button>
                    </div>
                </Form>

                <Divider />

                <Table
                    rowKey='id_empr_adic_adesao'
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    scroll={{ x: 1300 }}
                    pagination={{
                        total,
                        current: page,
                        pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: () => `Total ${total} registro(s)`,
                        onChange: (nextPage, nextPageSize) => {
                            setPage(nextPage);
                            setPageSize(nextPageSize);
                        },
                    }}
                />
            </Spin>
        </TemplateAdapter>
    );
};

function mapStateToProps(state) {
    return { redirect: state.redirectReducer };
}

function mapDispatchToProps(dispatch) {
    return {
        action: bindActionCreators(
            { ...RedirectActions, ...StatusManagerActions },
            dispatch,
        ),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(AdicionaisLogs);
